import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Decimal precision rules only apply to values typed by the user.
 * Pristine controls hold prescribed/patched data that must never be flagged.
 */
function shouldSkip(control: AbstractControl): boolean {
  return (
    control.pristine ||
    control.value === null ||
    control.value === undefined ||
    control.value === '' ||
    !Number.isFinite(Number(control.value))
  );
}

function countDecimals(value: number | string): number {
  // Exponential notation hides precision behind the exponent, so expand it before counting.
  const [mantissa, exponent = '0'] = String(value).toLowerCase().split('e');
  const [, decimals = ''] = mantissa.split('.');
  return Math.max(0, decimals.length - Number(exponent));
}

export class DecimalPrecisionValidator {
  static isInteger(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // Skip prescribed/patched (pristine) and empty values; those are handled elsewhere.
      if (control.pristine || control.value === null || control.value === undefined || control.value === '') {
        return null;
      }

      // Flag the decimal separator itself, before Number() drops "5." or turns "5," into NaN.
      if (/[.,]/.test(String(control.value))) {
        return { notInteger: true };
      }

      const numberValue = Number(control.value);

      // Non-numeric input is handled by isNumber, not here.
      if (!Number.isFinite(numberValue)) {
        return null;
      }

      return Number.isInteger(numberValue) ? null : { notInteger: true };
    };
  }

  static maxDecimals(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (shouldSkip(control)) {
        return null;
      }

      return countDecimals(control.value) > max ? { maxDecimals: { max } } : null;
    };
  }
}
